import { Disaster } from '../models/Disaster';

function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function containsPhrase(slug: string, phrase: string): boolean {
  const words = slug.split('-');
  const phraseWords = phrase.split('-');

  return phraseWords.length > 0 && words.some((_, index) =>
    phraseWords.every((word, phraseIndex) => words[index + phraseIndex] === word)
  );
}

export function getDisasterSlug(disaster: Disaster): string {
  const nameSlug = slugify(disaster.name || '');
  const parts = nameSlug ? [nameSlug] : [];
  const locationSlugs = (disaster.locations || [])
    .map((location) => slugify(location.country && location.country.name ? location.country.name : ''))
    .filter((locationSlug, index, locations) => locationSlug && locations.indexOf(locationSlug) === index);

  locationSlugs.forEach((locationSlug) => {
    if (!containsPhrase(parts.join('-'), locationSlug)) {
      parts.push(locationSlug);
    }
  });

  const yearMatch = disaster.dateOfDisaster && disaster.dateOfDisaster.match(/\b\d{4}\b/);
  if (yearMatch && !containsPhrase(parts.join('-'), yearMatch[0])) {
    parts.push(yearMatch[0]);
  }

  return parts.join('-') || `disaster-${disaster.disasterId || 'unknown'}`;
}

/**
 * The ID, not mutable descriptive fields, identifies a public disaster.
 * Old slug-only links require a unique current match; historical renamed slugs
 * require persisted aliases in the backend.
 */
export function getDisasterRoute(disaster: Disaster): [string, number, string] {
  const id = disaster.disasterId;
  if (!Number.isSafeInteger(id) || id <= 0) {
    throw new Error('Cannot create a disaster URL without a valid disaster ID');
  }
  return ['/disaster', id, getDisasterSlug(disaster)];
}

export function getDisasterIdFromUrl(value: string): number | null {
  if (!/^\d+$/.test(value)) {
    return null;
  }
  const id = Number(value);
  if (!Number.isSafeInteger(id) || id <= 0) {
    throw new Error(`Invalid disaster ID in URL: ${value}`);
  }
  return id;
}

export function resolveLegacyDisasterId(disasters: Disaster[], slug: string): number {
  if (!Array.isArray(disasters)) {
    throw new Error('Unexpected disaster list response');
  }
  const matches = disasters.filter((disaster) => getDisasterSlug(disaster) === slug);
  if (matches.length !== 1) {
    throw new Error(matches.length ? `Ambiguous disaster URL slug: ${slug}` : `No disaster found for URL slug: ${slug}`);
  }
  return getDisasterRoute(matches[0])[1];
}
